"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { CurrencyAmount, Token, TradeType } from "@uniswap/sdk-core";
import UniswapXBolt from "assets/svg/bolt.svg";
import { getCurrency } from "components/AccountDrawer/MiniPortfolio/Activity/getCurrency";
import {
  CancelledTransactionTitleTable,
  LimitOrderTextTable,
  OrderTextTable,
  getActivityTitle
} from "components/AccountDrawer/MiniPortfolio/constants";
import { getTokenSymbolOverride } from "components/CurrencyInputPanel/utils";
import { NATIVE_CHAIN_ID, nativeOnChain } from "constants/tokens";
import { useAllTokensMultichain } from "hooks/TokensLegacy";
import { getNativeLogoURI } from "lib/hooks/useCurrencyLogoURIs";
import { isOnChainOrder, useAllSignatures } from "state/signatures/hooks";
import { SignatureType } from "state/signatures/types";
import { isConfirmedTx, useMultichainTransactions } from "state/transactions/hooks";
import {
  TransactionType
} from "state/transactions/types";
import { TransactionStatus } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { t } from "uniswap/src/i18n";
import { isAddress } from "utilities/src/addresses";
import { logger } from "utilities/src/logger/logger";
import { NumberType, useFormatter } from "utils/formatNumbers";
function buildCurrencyDescriptor(currencyA, amtA, currencyB, amtB, formatNumber, isSwap = false) {
  const safeAmtA = amtA || "0";
  const safeAmtB = amtB || "0";
  const formattedA = currencyA && safeAmtA ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currencyA, safeAmtA).toSignificant()),
    type: NumberType.TokenNonTx
  }) : t("common.unknown");
  const symbolA = currencyA?.isToken ? ` ${getTokenSymbolOverride(currencyA.address, currencyA.symbol || "")}` : currencyA?.symbol ? ` ${currencyA.symbol}` : "";
  const formattedB = currencyB && safeAmtB ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currencyB, safeAmtB).toSignificant()),
    type: NumberType.TokenNonTx
  }) : t("common.unknown");
  const symbolB = currencyB?.isToken ? ` ${getTokenSymbolOverride(currencyB.address, currencyB.symbol || "")}` : currencyB?.symbol ? ` ${currencyB.symbol}` : "";
  const amountWithSymbolA = `${formattedA}${symbolA}`;
  const amountWithSymbolB = `${formattedB}${symbolB}`;
  return isSwap ? t("activity.transaction.swap.descriptor", {
    amountWithSymbolA,
    amountWithSymbolB
  }) : t("activity.transaction.tokens.descriptor", {
    amountWithSymbolA,
    amountWithSymbolB
  });
}
async function parseSwap(swap, chainId, formatNumber, tokens) {
  const [tokenIn, tokenOut] = await Promise.all([
    getCurrency(swap.inputCurrencyId, chainId, tokens),
    getCurrency(swap.outputCurrencyId, chainId, tokens)
  ]);
  const [inputRaw, outputRaw] = swap.tradeType === TradeType.EXACT_INPUT ? [swap.inputCurrencyAmountRaw, swap.settledOutputCurrencyAmountRaw ?? swap.expectedOutputCurrencyAmountRaw] : [swap.expectedInputCurrencyAmountRaw, swap.outputCurrencyAmountRaw];
  const extendedSwap = swap;
  const storedInputSymbol = extendedSwap.inputCurrencySymbol;
  const storedOutputSymbol = extendedSwap.outputCurrencySymbol;
  const inputAddress = tokenIn?.isToken ? tokenIn.address : swap.inputCurrencyId && swap.inputCurrencyId !== NATIVE_CHAIN_ID && swap.inputCurrencyId.toLowerCase() !== "native" ? swap.inputCurrencyId.includes("-") ? swap.inputCurrencyId.split("-")[1] : swap.inputCurrencyId : void 0;
  const outputAddress = tokenOut?.isToken ? tokenOut.address : swap.outputCurrencyId && swap.outputCurrencyId !== NATIVE_CHAIN_ID && swap.outputCurrencyId.toLowerCase() !== "native" ? swap.outputCurrencyId.includes("-") ? swap.outputCurrencyId.split("-")[1] : swap.outputCurrencyId : void 0;
  const inputSym = getTokenSymbolOverride(inputAddress, tokenIn?.symbol || storedInputSymbol || "Unknown");
  const outputSym = getTokenSymbolOverride(outputAddress, tokenOut?.symbol || storedOutputSymbol || "Unknown");
  let descriptor;
  descriptor = t("activity.transaction.swap.descriptor", {
    amountWithSymbolA: inputSym,
    amountWithSymbolB: outputSym
  });
  let inputLogoURI = extendedSwap.inputLogoURI;
  let outputLogoURI = extendedSwap.outputLogoURI;
  if (!inputLogoURI && tokenIn?.isNative) {
    inputLogoURI = getNativeLogoURI(chainId);
  }
  if (!outputLogoURI && tokenOut?.isNative) {
    outputLogoURI = getNativeLogoURI(chainId);
  }
  const logos = inputLogoURI && outputLogoURI ? [inputLogoURI, outputLogoURI] : void 0;
  return {
    descriptor,
    currencies: [tokenIn, tokenOut],
    logos,
    prefixIconSrc: swap.isUniswapXOrder ? UniswapXBolt : void 0
  };
}
function parseWrap(wrap, chainId, status, formatNumber) {
  const native = nativeOnChain(chainId);
  const wrapped = native.wrapped;
  const [input, output] = wrap.unwrapped ? [wrapped, native] : [native, wrapped];
  const descriptor = buildCurrencyDescriptor(
    input,
    wrap.currencyAmountRaw,
    output,
    wrap.currencyAmountRaw,
    formatNumber,
    true
  );
  const title = getActivityTitle(TransactionType.WRAP, status, wrap.unwrapped);
  const currencies = wrap.unwrapped ? [wrapped, native] : [native, wrapped];
  return { title, descriptor, currencies };
}
async function parseApproval(approval, chainId, status, tokens) {
  const extendedApproval = approval;
  const storedSymbol = extendedApproval.tokenSymbol;
  const storedLogoURI = extendedApproval.tokenLogoURI;
  let currency = await getCurrency(approval.tokenAddress, chainId, tokens);
  if (!currency && storedSymbol && approval.tokenAddress && approval.tokenAddress !== "0x0") {
    try {
      currency = new Token(chainId, approval.tokenAddress, 18, storedSymbol, storedSymbol);
    } catch (error) {
      console.warn("Failed to create Token for approval:", error);
    }
  }
  let descriptor;
  const tokenAddress = approval.tokenAddress && approval.tokenAddress !== "0x0" ? approval.tokenAddress : void 0;
  if (tokenAddress) {
    const fallbackSymbol = storedSymbol || currency?.symbol || currency?.name;
    descriptor = getTokenSymbolOverride(tokenAddress, fallbackSymbol);
    if (!descriptor || descriptor === "UNK") {
      descriptor = `${tokenAddress.slice(0, 6)}...${tokenAddress.slice(-4)}`;
    }
  } else if (storedSymbol) {
    descriptor = storedSymbol;
  } else if (currency?.symbol) {
    descriptor = currency.symbol;
  } else if (currency?.name) {
    descriptor = currency.name;
  } else {
    descriptor = t("common.unknown");
  }
  if (storedLogoURI && currency && !currency.logoURI) {
    ;
    currency.logoURI = storedLogoURI;
  }
  const logos = storedLogoURI ? [storedLogoURI] : void 0;
  return {
    title: getActivityTitle(
      TransactionType.APPROVAL,
      status,
      BigNumber.from(approval.amount).eq(0)
    ),
    descriptor,
    currencies: [currency],
    logos
  };
}
async function parseLP(lp, chainId, formatNumber, tokens) {
  const [baseCurrency, quoteCurrency] = await Promise.all([
    getCurrency(lp.baseCurrencyId, chainId, tokens),
    getCurrency(lp.quoteCurrencyId, chainId, tokens)
  ]);
  const [baseRaw, quoteRaw] = [lp.expectedAmountBaseRaw, lp.expectedAmountQuoteRaw];
  const descriptor = buildCurrencyDescriptor(baseCurrency, baseRaw, quoteCurrency, quoteRaw, formatNumber);
  return { descriptor, currencies: [baseCurrency, quoteCurrency] };
}
async function parseCollectFees(collect, chainId, formatNumber, tokens) {
  const {
    currencyId0: baseCurrencyId,
    currencyId1: quoteCurrencyId,
    expectedCurrencyOwed0: expectedAmountBaseRaw,
    expectedCurrencyOwed1: expectedAmountQuoteRaw
  } = collect;
  return parseLP(
    { baseCurrencyId, quoteCurrencyId, expectedAmountBaseRaw, expectedAmountQuoteRaw },
    chainId,
    formatNumber,
    tokens
  );
}
async function parseMigrateCreateV3(lp, chainId, tokens) {
  const [baseCurrency, quoteCurrency] = await Promise.all([
    getCurrency(lp.baseCurrencyId, chainId, tokens),
    getCurrency(lp.quoteCurrencyId, chainId, tokens)
  ]);
  const baseSymbol = baseCurrency?.symbol ?? t("common.unknown");
  const quoteSymbol = quoteCurrency?.symbol ?? t("common.unknown");
  const descriptor = t("activity.transaction.tokens.descriptor", {
    amountWithSymbolA: baseSymbol,
    amountWithSymbolB: quoteSymbol
  });
  return { descriptor, currencies: [baseCurrency, quoteCurrency] };
}
async function parseSend(send, chainId, formatNumber, tokens) {
  const { currencyId, amount, recipient } = send;
  const currency = await getCurrency(currencyId, chainId, tokens);
  const formattedAmount = currency ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currency, amount).toSignificant()),
    type: NumberType.TokenNonTx
  }) : t("common.unknown");
  const otherAccount = isAddress(recipient) || void 0;
  return {
    descriptor: t("activity.transaction.send.descriptor", {
      amountWithSymbol: `${formattedAmount} ${currency?.symbol}`,
      walletAddress: recipient
    }),
    otherAccount,
    currencies: [currency]
  };
}
export async function transactionToActivity(details, chainId, formatNumber, tokens) {
  if (!details) {
    return void 0;
  }
  try {
    const defaultFields = {
      hash: details.hash,
      chainId,
      title: getActivityTitle(details.info.type, details.status),
      status: details.status,
      timestamp: (isConfirmedTx(details) ? details.confirmedTime : details.addedTime) / 1e3,
      from: details.from,
      nonce: details.nonce,
      cancelled: details.cancelled
    };
    let additionalFields = {};
    const info = details.info;
    if (info.type === TransactionType.SWAP) {
      additionalFields = await parseSwap(info, chainId, formatNumber, tokens);
    } else if (info.type === TransactionType.APPROVAL) {
      additionalFields = await parseApproval(info, chainId, details.status, tokens);
    } else if (info.type === TransactionType.WRAP) {
      additionalFields = parseWrap(info, chainId, details.status, formatNumber);
    } else if (info.type === TransactionType.ADD_LIQUIDITY_V3_POOL || info.type === TransactionType.REMOVE_LIQUIDITY_V3 || info.type === TransactionType.ADD_LIQUIDITY_V2_POOL) {
      additionalFields = await parseLP(info, chainId, formatNumber, tokens);
    } else if (info.type === TransactionType.COLLECT_FEES) {
      additionalFields = await parseCollectFees(info, chainId, formatNumber, tokens);
    } else if (info.type === TransactionType.MIGRATE_LIQUIDITY_V3 || info.type === TransactionType.CREATE_V3_POOL) {
      additionalFields = await parseMigrateCreateV3(info, chainId, tokens);
    } else if (info.type === TransactionType.SEND) {
      additionalFields = await parseSend(info, chainId, formatNumber, tokens);
    }
    const activity = { ...defaultFields, ...additionalFields };
    if (details.cancelled) {
      activity.title = CancelledTransactionTitleTable[details.info.type];
      activity.status = TransactionStatus.Confirmed;
    }
    return activity;
  } catch (error) {
    logger.warn("parseLocal", "transactionToActivity", `Failed to parse transaction ${details.hash}`, error);
    return void 0;
  }
}
export function getTransactionToActivityQueryOptions(transaction, chainId, formatNumber, tokens) {
  return queryOptions({
    queryKey: ["transactionToActivity", transaction, chainId],
    queryFn: async () => transactionToActivity(transaction, chainId, formatNumber, tokens)
  });
}
export function getSignatureToActivityQueryOptions(signature, formatNumber) {
  return queryOptions({
    queryKey: ["signatureToActivity", signature],
    queryFn: async () => signatureToActivity(signature, formatNumber)
  });
}
function convertToSecTimestamp(timestamp) {
  const threshold = 41024448e5;
  if (timestamp >= threshold) {
    return Math.floor(timestamp / 1e3);
  } else {
    return timestamp;
  }
}
export async function signatureToActivity(signature, formatNumber) {
  if (!signature) {
    return void 0;
  }
  switch (signature.type) {
    case SignatureType.SIGN_UNISWAPX_ORDER:
    case SignatureType.SIGN_UNISWAPX_V2_ORDER:
    case SignatureType.SIGN_LIMIT: {
      if (isOnChainOrder(signature.status)) {
        return void 0;
      }
      const { title, statusMessage, status } = signature.type === SignatureType.SIGN_LIMIT ? LimitOrderTextTable[signature.status] : OrderTextTable[signature.status];
      return {
        hash: signature.orderHash,
        chainId: signature.chainId,
        title,
        status,
        offchainOrderDetails: signature,
        timestamp: convertToSecTimestamp(signature.addedTime),
        from: signature.offerer,
        statusMessage,
        prefixIconSrc: UniswapXBolt,
        ...await parseSwap(signature.swapInfo, signature.chainId, formatNumber)
      };
    }
    default:
      return void 0;
  }
}
export function useLocalActivities(account) {
  const allTransactions = useMultichainTransactions();
  const allSignatures = useAllSignatures();
  const tokens = useAllTokensMultichain();
  const { formatNumber } = useFormatter();
  const { data } = useQuery({
    queryKey: ["localActivities", account],
    queryFn: async () => {
      const transactions = Object.values(allTransactions).filter(([transaction]) => transaction.from === account).map(([transaction, chainId]) => transactionToActivity(transaction, chainId, formatNumber, tokens));
      const signatures = Object.values(allSignatures).filter((signature) => signature.offerer === account).map((signature) => signatureToActivity(signature, formatNumber));
      return (await Promise.all([...transactions, ...signatures])).reduce((acc, activity) => {
        if (activity) {
          acc[activity.hash] = activity;
        }
        return acc;
      }, {});
    }
  });
  return data ?? {};
}
